"use client"

import { Box, Button, Card, Chip, Container, FormControl, Grid, InputLabel, List, ListItem, ListItemText, MenuItem, Paper, Select, TextField, Typography, Dialog, DialogTitle, DialogContent, DialogActions, ListItemButton, CircularProgress, Alert, Autocomplete } from "@mui/material"
import { useState, useEffect } from "react"
import { WorkshopAdminApi } from '../../../api/workshop-admin-api';
import { WorkshopApi } from '../../../api/workshop-api';
import { useAlert } from "@/contexts/AlertContext";

// Định nghĩa type cho template
interface EmailTemplate {
  id: number;
  trigger_id: number;
  name: string;
  subject: string;
  body: string;
  is_default: boolean;
  created_at: string | null;
  updated_at: string | null;
  workshop_ids: (number|string)[];
}

// Định nghĩa type cho trigger
interface EmailTrigger {
  id: number;
  key: string;
  label: string;
  created_at: string;
  updated_at: string;
}

export default function EmailManagement() {
  const [selectedTrigger, setSelectedTrigger] = useState(0);
  const [templates, setTemplates] = useState<{ [key: string]: EmailTemplate[] }>({});
  const [selectedTemplateIdx, setSelectedTemplateIdx] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const alert = useAlert();
  // API state
  const [triggers, setTriggers] = useState<EmailTrigger[]>([]);
  const [triggersLoading, setTriggersLoading] = useState(true);
  const [triggersError, setTriggersError] = useState<string | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Form state
  const triggerKey = triggers[selectedTrigger]?.key || '';
  const triggerLabel = triggers[selectedTrigger]?.label || '';
  const templateList = templates[triggerKey] || [];
  const selectedTemplate = templateList[selectedTemplateIdx] || null;
  const [form, setForm] = useState<Partial<EmailTemplate>>({});
  const [editMode, setEditMode] = useState(false);
  const [isNewTemplate, setIsNewTemplate] = useState(false);

  const [workshopOptions, setWorkshopOptions] = useState<{id:number,title:string}[]>([]);
  const [selectedWorkshops, setSelectedWorkshops] = useState<any[]>([]);
  const [allWorkshops, setAllWorkshops] = useState(false);

  // Fetch triggers từ API
  useEffect(() => {
    setTriggersLoading(true);
    setTriggersError(null);
    WorkshopAdminApi.getListEmailTrigger()
      .then((data) => {
        setTriggers(data);
      })
      .catch((err) => {
        setTriggersError('Cannot get list email trigger');
        console.error('Error fetching triggers:', err);
      })
      .finally(() => {
        setTriggersLoading(false);
      });
  }, []);

  // Fetch templates khi trigger thay đổi
  useEffect(() => {
    if (!triggerKey) return;
    
    setTemplatesLoading(true);
    setTemplatesError(null);
    WorkshopAdminApi.getListEmailTemplate(triggers[selectedTrigger]?.id || 0)
      .then((data) => {
        // Map workshop_ids cho mỗi template
        const templatesWithWorkshopIds = (data as any[]).map(tpl => ({
          ...tpl,
          workshop_ids: tpl.workshop_ids || []
        }));
        setTemplates(prev => ({ ...prev, [triggerKey]: templatesWithWorkshopIds }));
        setSelectedTemplateIdx(0);
      })
      .catch((err) => {
        setTemplatesError('Cannot get list email template');
        console.error('Error fetching templates:', err);
      })
      .finally(() => {
        setTemplatesLoading(false);
      });
  }, [triggerKey]);

  // Fetch workshop published cho selector
  useEffect(() => {
    WorkshopApi.getListWorkshop().then((response) => {
      // Lọc ra workshop có calculated_status: "published"
      const publishedWorkshops = response.data?.data?.filter((workshop: any) => 
        workshop.calculated_status === "published"
      ) || [];
      
      // Map về format cần thiết cho Autocomplete
      const workshopOptions = publishedWorkshops.map((workshop: any) => ({
        id: workshop.id,
        title: workshop.title
      }));
      
      setWorkshopOptions(workshopOptions);
    }).catch((err) => {
      console.error('Error fetching published workshops:', err);
      setWorkshopOptions([]);
    });
  }, []);

  // Khi chọn template, load vào form
  useEffect(() => {
    if (selectedTemplate) {
      setForm({ ...selectedTemplate });
      setEditMode(false);
      setIsNewTemplate(false);
      // Set workshop selection when editing
      if (selectedTemplate.workshop_ids && selectedTemplate.workshop_ids.includes('all')) {
        setAllWorkshops(true);
        setSelectedWorkshops([]);
      } else if (selectedTemplate.workshop_ids) {
        setAllWorkshops(false);
        setSelectedWorkshops(
          workshopOptions.filter(w => selectedTemplate.workshop_ids.includes(String(w.id)))
        );
      } else {
        setAllWorkshops(false);
        setSelectedWorkshops([]);
      }
    }
  }, [selectedTemplate, workshopOptions]);

  // Thêm template mới
  const handleAddTemplate = () => {
    const newTemplate: Partial<EmailTemplate> = {
      name: 'New Template',
      is_default: templateList.length === 0,
      subject: '',
      body: '',
    };
    setForm(newTemplate);
    setEditMode(true);
    setIsNewTemplate(true);
    setSaveSuccess(null);
    setSaveError(null);
  };

  // Sửa template
  const handleSave = async () => {
    if (!form.name || !form.subject || !form.body) return;
    
    setSaveLoading(true);
    setSaveSuccess(null);
    setSaveError(null);
    
    // Chuẩn bị workshop_ids
    let workshop_ids: (number|string)[] = [];
    if (allWorkshops) {
      workshop_ids = ['all'];
    } else {
      workshop_ids = selectedWorkshops.map(w => w.id);
    }

    try {
      alert.showWait();
      if (isNewTemplate) {
        // Thêm template mới
        const res = await WorkshopAdminApi.addEmailTemplate(
          triggers[selectedTrigger]?.id || 0,
          form.name,
          form.subject,
          form.body,
          workshop_ids
        );
        // setSaveSuccess(res.data.message);
        console.log(res);
        alert.success(res.message);

      } else {
        // Cập nhật template hiện tại
        const res = await WorkshopAdminApi.updateEmailTemplate(
          selectedTemplate?.id || 0,
          form.name,
          form.subject,
          form.body,
          workshop_ids
        );
        // setSaveSuccess(res.data.message);
        alert.success(res.message);
      }
      
      // Refresh templates list
      const updatedTemplates = await WorkshopAdminApi.getListEmailTemplate(triggers[selectedTrigger]?.id || 0);
      setTemplates(prev => ({ ...prev, [triggerKey]: updatedTemplates }));
      setEditMode(false);
      setIsNewTemplate(false);
      
      // Auto hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(null), 3000);
      
    } catch (err) {
      setSaveError(isNewTemplate ? 'Failed to add template' : 'Failed to update template');
      console.error('Error saving template:', err);
    } finally {
      setSaveLoading(false);
      alert.hideWait();
    }
  };

  // Xóa template
  const handleDelete = async () => {
    if (templateList.length <= 1) return; // luôn giữ ít nhất 1 template
    if (!selectedTemplate) return;
    
    setSaveLoading(true);
    setSaveSuccess(null);
    setSaveError(null);
    
    try {
      alert.showWait();
      const res = await WorkshopAdminApi.deleteEmailTemplate(selectedTemplate.id);
      // setSaveSuccess('Template deleted successfully!');
      alert.success(res.message);
      
      // Refresh templates list
      const updatedTemplates = await WorkshopAdminApi.getListEmailTemplate(triggers[selectedTrigger]?.id || 0);
      setTemplates(prev => ({ ...prev, [triggerKey]: updatedTemplates }));
      setSelectedTemplateIdx(0);
      
      // Auto hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(null), 3000);
      
    } catch (err) {
      setSaveError('Failed to delete template');
      console.error('Error deleting template:', err);
    } finally {
      setSaveLoading(false);
      setConfirmDeleteOpen(false);
      alert.hideWait();
    }
  };

  // Đặt làm default
  const handleSetDefault = async () => {
    if (!selectedTemplate) return;
    
    setSaveLoading(true);
    setSaveSuccess(null);
    setSaveError(null);
    
    try {
      alert.showWait();
      const res = await WorkshopAdminApi.setDefaultEmailTemplate(selectedTemplate.id);
      // setSaveSuccess('Template set as default successfully!');
      alert.success(res.message);
      
      // Refresh templates list
      const updatedTemplates = await WorkshopAdminApi.getListEmailTemplate(triggers[selectedTrigger]?.id || 0);
      setTemplates(prev => ({ ...prev, [triggerKey]: updatedTemplates }));
      
      // Auto hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(null), 3000);
      
    } catch (err) {
      setSaveError('Failed to set template as default');
      console.error('Error setting default template:', err);
    } finally {
      setSaveLoading(false);
      alert.hideWait();
    }
  };

  // Hàm render preview với dữ liệu mẫu
  const renderPreview = (tpl: EmailTemplate | null) => {
    if (!tpl || !tpl.body) return '';
    let result = tpl.body;
    result = result.replace(/{{USER_NAME}}/g, 'Nguyen Van A');
    result = result.replace(/{{WORKSHOP_TITLE}}/g, 'Demo Workshop 2025');
    result = result.replace(/{{WORKSHOP_DATE}}/g, '31/07/2025');
    result = result.replace(/{{WORKSHOP_TIME}}/g, '14:00');
    result = result.replace(/{{HOST_NAME}}/g, 'Pythaverse Team');
    result = result.replace(/{{EVENT_LINK}}/g, 'https://www.google.com');
    return result;
  };

  // Hàm render preview subject với dữ liệu mẫu
  const renderPreviewSubject = (tpl: EmailTemplate | null) => {
    if (!tpl || !tpl.subject) return '';
    let result = tpl.subject;
    result = result.replace(/{{USER_NAME}}/g, 'Nguyen Van A');
    result = result.replace(/{{WORKSHOP_TITLE}}/g, 'Demo Workshop 2025');
    result = result.replace(/{{WORKSHOP_DATE}}/g, '31/07/2025');
    result = result.replace(/{{WORKSHOP_TIME}}/g, '14:00');
    result = result.replace(/{{HOST_NAME}}/g, 'Pythaverse Team');
    result = result.replace(/{{EVENT_LINK}}/g, 'https://www.google.com');
    return result;
  };

  if (triggersLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (triggersError) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2 }}>{triggersError}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Workshop Email Management
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Create and manage automated email templates for your workshops.
        </Typography>
      </Box>

      <Card>
        <Grid container sx={{ minHeight: "70vh" }}>
          {/* Left Panel */}
          <Grid item xs={12} md={4} sx={{ bgcolor: "grey.50", borderRight: 1, borderColor: "divider" }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="overline" color="text.secondary" sx={{ mb: 2, display: "block" }}>
                Email Triggers
              </Typography>
              <List>
                {triggers.map((trigger, index) => (
                  <ListItem key={trigger.key} disablePadding>
                    <ListItemButton
                      selected={selectedTrigger === index}
                      onClick={() => { setSelectedTrigger(index); setSelectedTemplateIdx(0); }}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        "&.Mui-selected": {
                          bgcolor: "primary.light",
                          color: "primary.main",
                        },
                        cursor: "pointer"
                      }}
                    >
                      <ListItemText primary={trigger.label} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              <Box sx={{ mt: 4 }}>
                <Typography variant="overline" color="text.secondary" sx={{ mb: 2, display: "block" }}>
                  Templates for "{triggerLabel}"
                </Typography>
                {templatesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : templatesError ? (
                  <Alert severity="error" sx={{ mb: 2 }}>{templatesError}</Alert>
                ) : (
                  <>
                    <List>
                      {templateList.map((template: EmailTemplate, index: number) => (
                        <ListItem key={template.id} disablePadding>
                          <ListItemButton
                            selected={selectedTemplateIdx === index}
                            onClick={() => { setSelectedTemplateIdx(index); setEditMode(false); }}
                            sx={{
                              borderRadius: 1,
                              mb: 1,
                              border: selectedTemplateIdx === index ? 2 : 0,
                              borderColor: "primary.main",
                              bgcolor: selectedTemplateIdx === index ? "background.paper" : "transparent",
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  {template.name}
                                  {template.is_default && <Chip label="Default" size="small" color="warning" />}
                                </Box>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                    <Button variant="outlined" fullWidth sx={{ mt: 2 }} onClick={handleAddTemplate}>
                      + New Template
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Right Panel */}
          <Grid item xs={12} md={8}>
            <Box sx={{ p: 5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
                <Typography variant="h2">
                  {isNewTemplate ? 'Create Template' : 'Edit Template'}
                </Typography>
                <Box>
                  <Button variant="outlined" sx={{ mr: 2 }} onClick={() => setPreviewOpen(true)} disabled={!form.name || !form.subject || !form.body}>Preview</Button>
                  <Button 
                    variant="contained" 
                    onClick={handleSave} 
                    disabled={saveLoading || !form.name || !form.subject || !form.body}
                  >
                    {saveLoading ? <CircularProgress size={20} /> : (isNewTemplate ? 'Create Template' : 'Save Changes')}
                  </Button>
                </Box>
              </Box>

              {/* Success/Error Messages */}
              {saveSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>{saveSuccess}</Alert>
              )}
              {saveError && (
                <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>
              )}

              <Box component="form">
                <TextField fullWidth label="Template Name" value={form.name || ''} sx={{ mb: 3 }}
                  onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setEditMode(true); }}
                />
                <TextField fullWidth label="Email Subject" value={form.subject || ''} sx={{ mb: 3 }}
                  onChange={e => { setForm(f => ({ ...f, subject: e.target.value })); setEditMode(true); }}
                />
                <TextField fullWidth label="Email Body" multiline rows={12} value={form.body || ''} sx={{ mb: 3 }}
                  onChange={e => { setForm(f => ({ ...f, body: e.target.value })); setEditMode(true); }}
                />
                <Box sx={{ mb: 4 }}>
                  <Typography variant="body2" gutterBottom>
                    Available Placeholders
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                    <Typography variant="body2" color="text.secondary">
                      <code>{"{{USER_NAME}}"}</code>, <code>{"{{WORKSHOP_TITLE}}"}</code>, <code>{"{{WORKSHOP_DATE}}"}</code>, <code>{"{{WORKSHOP_TIME}}"}</code>, <code>{"{{HOST_NAME}}"}</code>, <code>{"{{EVENT_LINK}}"}</code>
                    </Typography>
                  </Paper>
                </Box>
                {/* Chọn workshop áp dụng template */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>Apply to Workshops</b>
                  </Typography>
                  <Autocomplete
                    multiple
                    options={[{id: 'all', title: 'All Workshops'}, ...workshopOptions]}
                    getOptionLabel={(option) => option.title}
                    value={allWorkshops ? [{id: 'all', title: 'All Workshops'}] : selectedWorkshops}
                    onChange={(e, value) => {
                      if (value.find(v => v.id === 'all')) {
                        setAllWorkshops(true);
                        setSelectedWorkshops([]);
                      } else {
                        setAllWorkshops(false);
                        setSelectedWorkshops(value);
                      }
                    }}
                    disableCloseOnSelect
                    renderInput={(params) => (
                      <TextField {...params} label="Select workshops to apply" placeholder="Select workshops or All" />
                    )}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Select the workshops this template will apply to. If you select "All Workshops", this template will be used for all workshops (unless a specific workshop has its own template).
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSave} 
                    disabled={saveLoading || !form.name || !form.subject || !form.body}
                  >
                    {saveLoading ? <CircularProgress size={20} /> : (isNewTemplate ? 'Create Template' : 'Save Changes')}
                  </Button>
                  {!isNewTemplate && (
                    <>
                      <Button variant="outlined" color="error" onClick={() => setConfirmDeleteOpen(true)} disabled={templateList.length <= 1}>
                        Delete
                      </Button>
                      {!form.is_default && (
                        <Button variant="outlined" color="warning" onClick={handleSetDefault}>
                          Set as Default
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* Dialog preview */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Email Preview</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Subject: {renderPreviewSubject(form as EmailTemplate)}
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'grey.50', whiteSpace: 'pre-line' }}>
            {renderPreview(form as EmailTemplate)}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Dialog xác nhận xóa */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this template?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}
